/**
 * View: Tiering Settings
 * Tiering settings screen with server-side pagination.
 */

import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MainLayout } from "@/components/layout/MainLayout";
import { usePageTitle } from "@/hooks/usePageTitle";
import {
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableEmptyRow,
  TableHead,
  TableHeader,
  TableLoadingRow,
  TableRow,
} from "@/components/ui/table/table";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/ui/status-badge";
import { formatDateTime } from "@/utils/Format";
import type {
  TieringSettingsItem,
  TieringSettingsPaging,
} from "../../domain/model/TieringSettingsModel";
import { TieringSettingsViewModel } from "../viewmodel/TieringSettingsViewModel";

const PAGE_SIZE = 10;

export const TieringSettingsView = (): React.JSX.Element => {
  usePageTitle("Tiering Settings");
  
  const navigate = useNavigate();
  const [items, setItems] = useState<TieringSettingsItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [paging, setPaging] = useState<TieringSettingsPaging | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { loadTieringSettings } = TieringSettingsViewModel({
    setItems,
    setIsLoading,
    setPaging,
    setError,
  });

  const fetchPage = useCallback(
    (page: number) => {
      void loadTieringSettings({ page, size: PAGE_SIZE });
    },
    [loadTieringSettings],
  );

  useEffect(() => {
    fetchPage(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const totalPages = paging?.totalPages ?? 1;

  return (
    <MainLayout title="Tiering Settings">
      <div className="flex flex-col gap-4">
        <div className="flex justify-end items-center gap-4">
          <div className="flex items-center gap-3">
            <Button
              variant="primary"
              size="md"
              onClick={() => navigate("/swarapoint/tiering-settings/create")}
            >
              + Add New
            </Button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Period</TableHead>
              <TableHead>Created By</TableHead>
              <TableHead>Created At</TableHead>
              <TableHead>Updated By</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Updated At</TableHead>
              <TableHead> </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableLoadingRow colSpan={7} message="Loading tiering settings..." />
            ) : items.length === 0 ? (
              <TableEmptyRow colSpan={7} message="No tiering settings found." />
            ) : (
              items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.period}</TableCell>
                  <TableCell>{item.createdBy}</TableCell>
                  <TableCell>{formatDateTime(item.createdAt)}</TableCell>
                  <TableCell>{item.updatedBy}</TableCell>
                  <TableCell>
                    <StatusBadge
                      variant={item.status === "ongoing" ? "success" : "error"}
                    >
                      {item.status === "ongoing" ? "Ongoing" : "Ended"}
                    </StatusBadge>
                  </TableCell>
                  <TableCell>{formatDateTime(item.updatedAt)}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        navigate(`/swarapoint/tiering-settings/edit/${item.id}`);
                      }}
                      aria-label={`Edit tiering setting ${item.period}`}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                        <path d="m15 5 4 4" />
                      </svg>
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={(page) => setCurrentPage(page)}
        />
      </div>
    </MainLayout>
  );
};
